import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, pathFor, paxRoute, StreamResponse } from "../../common";
import { AgentVersionFileParams, ListAgentsQuery, ListAgentsResponse } from "./schemas";

export const service = {
  group: "agents",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Agents API",
  description: "Published versioned game agents and their immutable artifacts.",
} as const satisfies ContractService;

export const agentVersionFileRoute =
  "/api/agents/v1alpha1/versions/{versionId}/files/{file}" as const;

export function agentVersionFilePath(versionId: string, file: string): string {
  return pathFor(agentVersionFileRoute, { versionId, file });
}

export const list = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/agents/v1alpha1",
      operationId: "agents.list",
      summary: "List game agents.",
      description: "Lists published Game Masters with the details of each one's live version.",
      inputStructure: "detailed",
      successDescription: "Every available agent.",
    }),
  )
  .errors(errors.badRequest)
  .input(z.object({ query: ListAgentsQuery }))
  .output(ListAgentsResponse);

export const getFile = oc
  .meta(
    paxRoute({ auth: { kind: "public" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: agentVersionFileRoute,
      operationId: "agents.versions.files.get",
      summary: "Download an immutable agent file.",
      description: "Streams one file of a published agent version, or one file inside its bundle.",
      inputStructure: "detailed",
      outputStructure: "detailed",
      responseBodyHint: "octet-stream",
      successDescription: "The file with its stored media type.",
    }),
  )
  .errors({
    ...errors.badRequest,
    ...errors.notFound,
  })
  .input(z.object({ params: AgentVersionFileParams }))
  .output(StreamResponse);
